import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PawPrint, Plus, Images } from "lucide-react";
import { Link } from "react-router-dom";

const EMPTY = { name: "", breed: "", weight_lbs: 0, coat: "", temperament: "", notes: "", photo_url: "" };

export default function Pets() {
  const [pets, setPets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/pets");
    setPets(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.breed) { toast.error("Name and breed are required"); return; }
    try {
      await api.post("/pets", form);
      toast.success(`${form.name} added to your family`);
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Failed to add pet"); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-accent">My pack</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">My pets</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile" data-testid="btn-add-pet">
              <Plus className="w-4 h-4 mr-1"/> Add pet
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display">Add a pet</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pet-name"/></div>
              <div><Label>Breed</Label><Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} data-testid="pet-breed"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Weight (lbs)</Label>
                  <Input type="number" value={form.weight_lbs} onChange={(e) => setForm({ ...form, weight_lbs: +e.target.value })} data-testid="pet-weight"/></div>
                <div><Label>Coat</Label>
                  <Input value={form.coat} onChange={(e) => setForm({ ...form, coat: e.target.value })}
                    placeholder="e.g. Curly, medium" data-testid="pet-coat"/></div>
              </div>
              <div><Label>Temperament / warnings</Label>
                <Input value={form.temperament} onChange={(e) => setForm({ ...form, temperament: e.target.value })}
                  placeholder="Sensitive paws, shy around clippers…" data-testid="pet-temp"/></div>
              <div><Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pet-notes"/></div>
              <div><Label>Photo URL (optional)</Label>
                <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="pet-photo"/></div>
              <Button onClick={submit} className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="btn-save-pet">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      {pets.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#E5DCCE] p-10 text-center text-[#6B625B]">
          <PawPrint className="w-8 h-8 mx-auto mb-2 opacity-40"/>
          <p className="text-sm">No pets yet — add your first pup!</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pet-grid">
          {pets.map(p => (
            <Link key={p.id} to={`/pets/${p.id}/gallery`} data-testid={`pet-card-${p.id}`}>
              <div className="bg-white rounded-3xl border border-[#E5DCCE] p-5 shadow-sm card-tactile h-full">
                {p.photo_url ? (
                  <img src={p.photo_url} alt={p.name} className="w-full h-40 rounded-2xl object-cover mb-3"/>
                ) : (
                  <div className="w-full h-40 rounded-2xl bg-[#F5E6CF] grid place-content-center text-[#A9480F] mb-3">
                    <PawPrint className="w-10 h-10"/>
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-xl font-bold">{p.name}</h3>
                    <div className="text-sm text-[#6B625B]">{p.breed} · {p.weight_lbs} lbs</div>
                  </div>
                  <div className="text-[#C85A17] flex items-center gap-1 text-xs font-semibold">
                    <Images className="w-3.5 h-3.5"/> Gallery
                  </div>
                </div>
                {p.coat && <div className="text-xs text-[#6B625B] mt-2"><span className="font-semibold text-[#1F1A17]">Coat:</span> {p.coat}</div>}
                {p.temperament && <div className="text-xs text-[#6B625B] mt-1"><span className="font-semibold text-[#1F1A17]">Notes:</span> {p.temperament}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
